// src/features/treinos/components/muscle-map/MuscleMap.jsx
//
// Componente reutilizável de mapa muscular. ADITIVO — não toca telas/rotas/fluxos.
// O SVG anatômico é injetado INLINE (cache de markup tagueado em nível de módulo,
// ver muscleMapUtils.loadTaggedSvg). A pintura é SEMPRE dirigida pela prop
// `muscles`; grupo sem dado = inactive.
//
// <MuscleMap
//   view="front" | "back" | "both"          (default "front")
//   variant="dashboard" | "complete" | "details" | "mini"  (default "complete")
//   region="full" | "upper" | "lower" | "auto"  (default "full"; mini: "auto")
//   muscles={{ [canonicalPtOuLogico]: state }}
//   showLabels={false}
//   showLegend={false}
//   onMuscleClick={(logicalKey, event) => {}}
//   selectable                              (variante `select`, 12-prompt § 5)
//   selected={["chest", "back"]}            grupos escolhidos pela pessoa
//   onToggleMuscle={(logicalKey, next) => {}}
// />
//
// A variante `select` NÃO é um segundo mapa: é o mesmo SVG, os mesmos ids e a
// mesma pintura. O que muda é que o grupo ganha alvo de toque, o estado
// `selected` (azul, em diagonal) entra na FRENTE de qualquer estado de fadiga
// — seleção é escolha explícita e não pode ser apagada por um cálculo — e os
// grupos escolhidos aparecem como chips embaixo.

import React, {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from "react";
import Style from "./MuscleMap.module.css";
import {
  STATE_CLASS_MAP,
  ALL_STATE_CLASSES,
  STATE_PRIORITY,
  STATE_COLORS,
  MUSCLE_SVG_IDS,
  LOGICAL_LABELS_PT,
} from "./muscleMapConfig.js";
import {
  loadTaggedSvg,
  resolveCanonicalToLogical,
  getRegionViewBox,
  inferRegionFromStates,
} from "./muscleMapUtils.js";

const VARIANT_CLASS = {
  dashboard: Style.variantDashboard,
  card: Style.variantCard, // card do dashboard: altura fixa, corpo inteiro escala p/ caber
  complete: Style.variantComplete,
  details: Style.variantDetails,
  mini: Style.variantMini,
};

// Estados exibidos na legenda (ordem de leitura).
const LEGEND_ORDER = ["focus", "high", "medium", "recovery", "ready", "trained"];
const LEGEND_LABEL = {
  focus: "Foco",
  high: "Alto",
  medium: "Médio",
  recovery: "Recuperando",
  ready: "Pronto",
  trained: "Treinado",
};

/**
 * Traduz a prop `muscles` (chaves canônicas PT ou já lógicas) para
 * { [logicalKey]: state }, resolvendo conflitos por prioridade de estado.
 */
function resolveLogicalStates(muscles) {
  const out = {};
  if (!muscles) return out;

  const apply = (logical, state) => {
    if (!logical) return;
    const prev = out[logical];
    if (!prev || (STATE_PRIORITY[state] ?? 0) > (STATE_PRIORITY[prev] ?? 0)) {
      out[logical] = state;
    }
  };

  for (const [rawKey, state] of Object.entries(muscles)) {
    if (!state) continue;
    // 1) já é chave lógica?  2) senão traduz canônico PT → lógico (array).
    // O resolver normaliza os dois lados (case + acentos) e dá console.warn
    // quando o muscle_group não existe no mapa — nada fica cinza em silêncio.
    if (MUSCLE_SVG_IDS[rawKey]) {
      apply(rawKey, state);
      continue;
    }
    const logicals = resolveCanonicalToLogical(rawKey);
    if (!logicals || logicals.length === 0) continue; // desconhecido/sem pintura
    for (const logical of logicals) apply(logical, state);
  }
  return out;
}

/** Aplica os estados nos <g class="muscle-group"> dentro de `root`. */
function paintMuscles(root, logicalStates) {
  if (!root) return;
  root.querySelectorAll(".muscle-group").forEach((group) => {
    group.classList.remove(...ALL_STATE_CLASSES);
    const state = logicalStates[group.dataset.muscle];
    const classes = STATE_CLASS_MAP[state];
    if (classes && classes.length) group.classList.add(...classes);
  });
}

/**
 * Pintura CONTÍNUA genérica: o CHAMADOR fornece, por músculo, { color, opacity }
 * já calculado (dashboard via corDe(fadiga); Treino Completo via activationPaint).
 * MuscleMap só aplica o que recebe — sem regra de domínio aqui. opacity 0/sem
 * cor volta ao base inativo.
 * @param {Element} root
 * @param {Record<string, {color?: string, opacity?: number}>} paintMap
 */
function paintWithMap(root, paintMap) {
  if (!root) return;
  root.querySelectorAll(".muscle-group").forEach((group) => {
    group.classList.remove(...ALL_STATE_CLASSES);
    const entry = paintMap?.[group.dataset.muscle];
    const opacity = entry?.opacity || 0;
    const color = entry?.color;
    group.querySelectorAll(".m-fill").forEach((path) => {
      if (!opacity || !color) {
        path.style.removeProperty("fill");
        path.style.removeProperty("fill-opacity");
        path.style.removeProperty("opacity");
      } else {
        path.style.fill = color;
        path.style.fillOpacity = String(opacity);
        path.style.opacity = "1";
      }
    });
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Painel de um lado (front | back)
// ─────────────────────────────────────────────────────────────────────────────
function SvgPanel({
  side,
  region,
  logicalStates,
  paint,
  onMuscleClick,
  onMuscleHover,
  onMuscleLeave,
  className,
}) {
  const ref = useRef(null);
  const [markup, setMarkup] = useState("");
  const [error, setError] = useState(false);

  // Carrega o markup tagueado UMA vez (cache de módulo); só clona aqui.
  useEffect(() => {
    let alive = true;
    setError(false);
    loadTaggedSvg(side)
      .then((html) => alive && setMarkup(html))
      .catch(() => alive && setError(true));
    return () => {
      alive = false;
    };
  }, [side]);

  // Injeção IMPERATIVA do markup, fora do diff do React. Com
  // dangerouslySetInnerHTML o React 19 compara o objeto {__html} por
  // IDENTIDADE (não pelo valor da string) e re-seta innerHTML em QUALQUER
  // re-render do painel — ex.: toggle Frente/Costas — varrendo as classes que
  // paintMuscles aplicou, sem que o effect de pintura (deps inalteradas)
  // re-disparasse. Injetando manualmente, o nó só é reescrito quando `markup`
  // muda de fato.
  useLayoutEffect(() => {
    if (ref.current && markup) ref.current.innerHTML = markup;
  }, [markup]);

  // Recorte por região: troca SÓ o atributo viewBox do <svg> injetado
  // (paths/ids/pintura intactos). Re-aplica após cada injeção e quando a
  // região efetiva mudar.
  const cropViewBox = getRegionViewBox(side, region);
  useLayoutEffect(() => {
    const svg = ref.current?.querySelector("svg");
    if (!svg || !markup) return;
    svg.setAttribute("viewBox", cropViewBox);
    svg.setAttribute("preserveAspectRatio", "xMidYMid meet");
  }, [markup, cropViewBox]);

  // Pinta após CADA injeção e sempre que os estados mudarem (idempotente:
  // paintMuscles limpa as classes de estado antes de aplicar). Declarado
  // DEPOIS do effect de injeção — a ordem de declaração garante que a pintura
  // roda sobre o markup recém-injetado no mesmo commit.
  useLayoutEffect(() => {
    if (!markup) return;
    // Pintura contínua (paint) tem prioridade sobre os estados discretos.
    if (paint) paintWithMap(ref.current, paint);
    else paintMuscles(ref.current, logicalStates);
  }, [markup, logicalStates, paint]);

  const handleClick = useCallback(
    (e) => {
      if (!onMuscleClick) return;
      const g = e.target.closest?.(".muscle-group");
      if (g?.dataset?.muscle) onMuscleClick(g.dataset.muscle, e);
    },
    [onMuscleClick],
  );

  // Hover/touch p/ tooltip (Fase 3): reporta o grupo sob o ponteiro.
  const handlePointer = useCallback(
    (e) => {
      if (!onMuscleHover) return;
      const g = e.target.closest?.(".muscle-group");
      if (g?.dataset?.muscle) onMuscleHover(g.dataset.muscle, e);
      else onMuscleLeave?.();
    },
    [onMuscleHover, onMuscleLeave],
  );
  const handleLeave = useCallback(() => onMuscleLeave?.(), [onMuscleLeave]);

  if (error) {
    return <div className={`${Style.panel} ${Style.panelError} ${className || ""}`} />;
  }

  return (
    // filhos gerenciados imperativamente (innerHTML no useLayoutEffect acima);
    // o React nunca declara children aqui, então não há conflito de reconciliação
    <div
      ref={ref}
      className={`${Style.panel} ${className || ""}`}
      data-side={side}
      data-loading={markup ? undefined : "true"}
      onClick={onMuscleClick ? handleClick : undefined}
      onPointerMove={onMuscleHover ? handlePointer : undefined}
      onPointerDown={onMuscleHover ? handlePointer : undefined}
      onPointerLeave={onMuscleHover ? handleLeave : undefined}
      onPointerCancel={onMuscleHover ? handleLeave : undefined}
    />
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MuscleMap
// ─────────────────────────────────────────────────────────────────────────────
export default function MuscleMap({
  view = "front",
  variant = "complete",
  region,
  muscles,
  paint,              // pintura contínua: { [logicalKey]: { color, opacity } }
  dualColumn = false, // força os 2 lados lado a lado (sem toggle interno)
  showLabels = false,
  showLegend = false,
  selectable = false,
  selected = null,
  onToggleMuscle,
  onMuscleClick,
  onMuscleHover,
  onMuscleLeave,
}) {
  // Seleção entra como estado `selected` em cima do que `muscles` já pinta.
  // Mesclar aqui (e não numa segunda passada de CSS) mantém UM caminho de
  // pintura: o mapa continua sendo dirigido só por `muscles`.
  const musclesComSelecao = useMemo(() => {
    if (!selectable || !Array.isArray(selected) || !selected.length) return muscles;
    const mesclado = { ...(muscles || {}) };
    selected.forEach((chave) => { mesclado[chave] = "selected"; });
    return mesclado;
  }, [muscles, selectable, selected]);

  const logicalStates = useMemo(
    () => resolveLogicalStates(musclesComSelecao),
    [musclesComSelecao],
  );

  // No modo de seleção, o clique alterna o grupo e devolve a lista nova —
  // quem decide o que fazer com ela é quem usa o componente.
  const aoClicar = useCallback(
    (chave, evento) => {
      if (selectable && onToggleMuscle) {
        const atual = Array.isArray(selected) ? selected : [];
        const proximo = atual.includes(chave)
          ? atual.filter((x) => x !== chave)
          : [...atual, chave];
        onToggleMuscle(chave, proximo);
      }
      if (onMuscleClick) onMuscleClick(chave, evento);
    },
    [selectable, onToggleMuscle, selected, onMuscleClick],
  );

  // Região efetiva: default "full" (comportamento original); mini default
  // "auto" (preview compacto recortado). "auto" infere pelos grupos pintados.
  const requestedRegion = region || (variant === "mini" ? "auto" : "full");
  const effectiveRegion = useMemo(
    () =>
      requestedRegion === "auto"
        ? inferRegionFromStates(logicalStates)
        : requestedRegion,
    [requestedRegion, logicalStates],
  );

  // "both" em telas pequenas alterna frente/costas; em telas largas mostra os dois.
  const isBoth = view === "both";
  const [activeSide, setActiveSide] = useState(isBoth ? "front" : view);
  useEffect(() => {
    if (!isBoth) setActiveSide(view);
    else setActiveSide((s) => (s === "front" || s === "back" ? s : "front"));
  }, [view, isBoth]);

  const activeLabels = useMemo(() => {
    if (!showLabels) return [];
    return Object.entries(logicalStates)
      .filter(([, st]) => st && st !== "inactive")
      .map(([logical]) => LOGICAL_LABELS_PT[logical] || logical);
  }, [showLabels, logicalStates]);

  const wrapperCls = [
    Style.muscleMap,
    VARIANT_CLASS[variant] || VARIANT_CLASS.complete,
    selectable ? Style.selectable : null,
    dualColumn ? Style.dualColumn : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className={wrapperCls}
      data-view={view}
      data-active-side={activeSide}
      data-region={effectiveRegion}
      role="img"
      aria-label="Mapa muscular"
    >
      {isBoth && !dualColumn && (
        <div className={Style.sideToggle} role="tablist" aria-label="Frente ou costas">
          <button
            type="button"
            className={Style.sideBtn}
            aria-pressed={activeSide === "front"}
            data-active={activeSide === "front" ? "true" : undefined}
            onClick={() => setActiveSide("front")}
          >
            Frente
          </button>
          <button
            type="button"
            className={Style.sideBtn}
            aria-pressed={activeSide === "back"}
            data-active={activeSide === "back" ? "true" : undefined}
            onClick={() => setActiveSide("back")}
          >
            Costas
          </button>
        </div>
      )}

      <div className={Style.stage}>
        {(view === "front" || isBoth) && (
          <SvgPanel
            side="front"
            region={effectiveRegion}
            logicalStates={logicalStates}
            paint={paint}
            onMuscleClick={selectable || onMuscleClick ? aoClicar : undefined}
            onMuscleHover={onMuscleHover}
            onMuscleLeave={onMuscleLeave}
            className={Style.panelFront}
          />
        )}
        {(view === "back" || isBoth) && (
          <SvgPanel
            side="back"
            region={effectiveRegion}
            logicalStates={logicalStates}
            paint={paint}
            onMuscleClick={selectable || onMuscleClick ? aoClicar : undefined}
            onMuscleHover={onMuscleHover}
            onMuscleLeave={onMuscleLeave}
            className={Style.panelBack}
          />
        )}
      </div>

      {showLabels && activeLabels.length > 0 && (
        <div className={Style.labels}>
          {activeLabels.map((label) => (
            <span key={label} className={Style.labelChip}>
              {label}
            </span>
          ))}
        </div>
      )}

      {/* Chips das seleções (§ 5): tocar no chip desmarca — o mesmo gesto do
          mapa, no lugar onde a pessoa está olhando depois de escolher. */}
      {selectable && Array.isArray(selected) && selected.length > 0 && (
        <div className={Style.chips}>
          {selected.map((chave) => (
            <button
              key={chave}
              type="button"
              className={Style.chip}
              onClick={() => onToggleMuscle?.(
                chave,
                selected.filter((x) => x !== chave),
              )}
            >
              {LOGICAL_LABELS_PT[chave] || chave}
              <span className={Style.chipX} aria-hidden="true">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M18 6 6 18M6 6l12 12"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
            </button>
          ))}
        </div>
      )}

      {showLegend && (
        <div className={Style.legend}>
          {LEGEND_ORDER.map((st) => (
            <span key={st} className={Style.legendItem}>
              <span
                className={Style.legendDot}
                style={{ background: STATE_COLORS[st] }}
              />
              {LEGEND_LABEL[st]}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
